import { boundForPlanner, readsDocumentText } from "./tools";
import type { ToolResult } from "./tools";
import { withoutDanglingMarkdown } from "./danglingMarkdown";
import { cutAtFabricatedTurn } from "./fabricatedTurns";

export type WireToolCall = { id: string; name: string; arguments: string };

export type WireMessage = {
  role: string;
  content?: string | null;
  toolCalls?: WireToolCall[] | null;
  toolCallId?: string | null;
};

const EMPTY_REPLY = "(tomt svar)";

const SKIPPED_RESULT: ToolResult = {
  ok: false,
  deviationCode: "SKIPPED",
  message: "Hoppet over — turen ble avsluttet før verktøyet ble kjørt.",
};

// The single source of the SKIPPED tool reply used both by the live loop's early-break balancing and
// by toResumableConversation when it aborts a dangling call on a hydrated conversation.
export const skippedToolReply = (toolCallId: string): WireMessage => ({
  role: "tool",
  toolCallId,
  content: boundForPlanner(SKIPPED_RESULT),
});

// Owns the assistant/tool message array and the balance invariant it upholds: an assistant message
// that lists tool calls must be followed by exactly one role:"tool" reply per call before the next
// plan request — the real result, USER_DECLINED on cancel, or a SKIPPED placeholder on an early
// break — or the planner rejects the next turn with a 400. pushSkippedRemainder keeps that balancing
// in one named place so the rest of the loop never touches the raw array.
export const createAgentMemory = (messages: WireMessage[]) => ({
  initSystem: (content: string): void => {
    if (messages.length === 0) {
      messages.push({ role: "system", content });
      return;
    }
    const first = messages[0];
    if (first.role === "system" && first.content !== content) {
      first.content = content;
    }
  },
  pushUser: (content: string): void => {
    messages.push({ role: "user", content });
  },
  pushAssistantWithToolCalls: (content: string | null, toolCalls: WireToolCall[]): void => {
    messages.push({ role: "assistant", content, toolCalls });
  },
  pushToolResult: (toolCallId: string, result: ToolResult, toolName?: string): void => {
    messages.push({ role: "tool", toolCallId, content: boundForPlanner(result, toolName) });
  },
  pushSkippedRemainder: (toolCalls: WireToolCall[], fromIndex: number): void => {
    for (let i = fromIndex; i < toolCalls.length; i++) {
      messages.push(skippedToolReply(toolCalls[i].id));
    }
  },
  withdrawTrailingUser: (content: string): void => {
    const last = messages[messages.length - 1];
    if (last?.role === "user" && last.content === content) messages.pop();
  },
  pushAssistantReply: (content: string): void => {
    messages.push({ role: "assistant", content });
  },
  // The planner may have already pushed an assistant reply this turn; the streamed answer replaces
  // that trailing message rather than appending a second assistant turn.
  replaceTrailingAssistantReply: (content: string): void => {
    messages[messages.length - 1] = { role: "assistant", content };
  },
  endReplyWith: (content: string): void => {
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && !last.toolCalls?.length && last.content) {
      messages[messages.length - 1] = {
        role: "assistant",
        content: `${withoutDanglingMarkdown(last.content)}\n\n${content}`,
      };
      return;
    }
    messages.push({ role: "assistant", content });
  },
  dropFabricatedTurns: (): void => {
    messages.forEach((message, i) => {
      if (message.role !== "assistant" || !message.content) return;
      const said = cutAtFabricatedTurn(message.content);
      if (said.fabricated) messages[i] = { ...message, content: said.text.trim() || (message.toolCalls?.length ? null : EMPTY_REPLY) };
    });
  },
  setAsideEarlierFileReads: (): void => {
    const reads = new Map(
      messages
        .flatMap((message) => message.toolCalls ?? [])
        .filter((call) => readsDocumentText(call.name))
        .map((call) => [call.id, call] as const),
    );
    messages.forEach((message, i) => {
      const call = message.role === "tool" && message.toolCallId ? reads.get(message.toolCallId) : undefined;
      if (call !== undefined) messages[i] = { ...message, content: setAsideRead(call.name, call.arguments) };
    });
  },
});

const setAsideRead = (toolName: string, argumentsJson: string): string => {
  const { documentId, fromPart } = parseReadArguments(argumentsJson);
  return JSON.stringify({
    documentId,
    fromPart: fromPart ?? 1,
    omitted: `Teksten er lest i en tidligere tur og er ikke med her. Les delen på nytt med ${toolName} om du trenger den.`,
  });
};

const parseReadArguments = (argumentsJson: string): { documentId?: unknown; fromPart?: unknown } => {
  try {
    const parsed = JSON.parse(argumentsJson || "{}");
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

export type AgentMemory = ReturnType<typeof createAgentMemory>;
